import React from "react";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { AutoAwesome, Search } from "@mui/icons-material";
import { useLayout } from "../../context/LayoutContext";
import { ChatPanel } from "./ChatPanel";
import { MatchPanelContent } from "./match/MatchPanelContent";

interface ChatPanelContainerProps {
  mode: 'chat' | 'match';
  chatProps?: {
    liveWebSocket: React.MutableRefObject<WebSocket | null>;
    agentApiEndpoint: string;
    reportId: string;
    reportType: string;
    evidenceIds?: string[];
    handleSaveChanges?: () => Promise<void>;
    fetchReportData?: () => void;
    showError?: (message: string) => void;
    onSendMessageRefReady?: (sendFn: (message: string) => Promise<void>) => void;
  };
  sx?: SxProps<Theme>;
}

export const ChatPanelContainer: React.FC<ChatPanelContainerProps> = ({
  mode,
  chatProps,
  sx,
}) => {
  const theme = useTheme();
  const { sidePanelOpen, setSidePanelOpen } = useLayout();

  const icon = mode === 'chat' ? <AutoAwesome color="primary" fontSize="small" /> : <Search color="primary" fontSize="small" />;
  const label = mode === 'chat' ? 'AI Assistant' : 'Report Assistant';
  const tooltip = mode === 'chat' ? 'Open AI Assistant' : 'Open Report Assistant';

  const handleClose = () => setSidePanelOpen(false);
  const handleOpen = () => setSidePanelOpen(true);

  return (
    <Box
      sx={[
        {
          width: sidePanelOpen ? 400 : 48,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          flexShrink: 0,
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
        },
        // Merge parent-provided sx overrides
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {sidePanelOpen ? (
        <Box
          sx={{
            width: 400,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {mode === 'chat' && chatProps ? (
            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
              <ChatPanel {...chatProps} onClose={handleClose} />
            </Box>
          ) : (
            <MatchPanelContent onClose={handleClose} />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: 48,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 2,
            gap: 2,
            cursor: "pointer",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
          onClick={handleOpen}
        >
          <Tooltip title={tooltip} placement="left">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpen();
              }}
              size="small"
            >
              {icon}
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              typography: "subtitle2",
              color: "text.secondary",
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            {label}
          </Box>
        </Box>
      )}
    </Box>
  );
};
